import type { AllRequestFilter } from './queries';

/**
 * The All Requests filter bar, as arithmetic rather than as a component.
 *
 * Pure and separate from the screen so the date maths can be tested without
 * rendering a table, and so the range a preset produces is one definition
 * rather than one per place that offers it.
 *
 * The presets are the Apps Script's, because the team already reaches for
 * "This Month" and finding a different list there would be a change to the
 * process rather than a port of it.
 */

export type RangePreset = 'this-month' | 'last-month' | 'this-year' | 'all' | 'custom';

export const RANGE_LABELS: Record<RangePreset, string> = {
  'this-month': 'This Month',
  'last-month': 'Last Month',
  'this-year': 'This Year',
  all: 'All Time',
  custom: 'Custom',
};

export const RANGE_ORDER: RangePreset[] = [
  'this-month',
  'last-month',
  'this-year',
  'all',
  'custom',
];

function iso(year: number, monthIndex: number, day: number): string {
  const month = String(monthIndex + 1).padStart(2, '0');
  return `${String(year)}-${month}-${String(day).padStart(2, '0')}`;
}

/** Last day of a month, without rolling into the next one. */
function lastDay(year: number, monthIndex: number): number {
  return new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
}

/**
 * The dates a preset means, given today.
 *
 * `today` is passed in rather than read from the clock so this is testable and
 * so the screen and its test cannot disagree about what month it is.
 *
 * Custom returns nulls: it is the preset that means "leave the two date boxes
 * alone", so it must not overwrite what somebody typed into them.
 */
export function rangeBounds(
  preset: RangePreset,
  today: string,
): { from: string; to: string } | null {
  if (preset === 'custom') {
    return null;
  }
  if (preset === 'all') {
    return { from: '', to: '' };
  }
  const parts = today.split('-');
  const year = Number(parts[0]);
  const month = Number(parts[1]) - 1;
  if (!Number.isFinite(year) || !Number.isFinite(month)) {
    return { from: '', to: '' };
  }
  if (preset === 'this-year') {
    return { from: iso(year, 0, 1), to: iso(year, 11, 31) };
  }
  // Last month rolls the year back in January rather than producing month -1,
  // which would have quietly become December of the SAME year.
  const target = preset === 'last-month' ? month - 1 : month;
  const targetYear = target < 0 ? year - 1 : year;
  const targetMonth = target < 0 ? 11 : target;
  return {
    from: iso(targetYear, targetMonth, 1),
    to: iso(targetYear, targetMonth, lastDay(targetYear, targetMonth)),
  };
}

/** Applying a preset to a filter, leaving everything else as it was. */
export function withPreset(
  filter: AllRequestFilter,
  preset: RangePreset,
  today: string,
): AllRequestFilter {
  const bounds = rangeBounds(preset, today);
  return bounds === null ? filter : { ...filter, from: bounds.from, to: bounds.to };
}

/**
 * Which preset a pair of dates corresponds to, so the dropdown can show the
 * truth after somebody edits a date box by hand.
 *
 * Without this, typing a date while "This Month" is selected leaves the
 * dropdown claiming a range the boxes no longer hold.
 */
export function presetOf(filter: AllRequestFilter, today: string): RangePreset {
  for (const preset of RANGE_ORDER) {
    if (preset === 'custom') continue;
    const bounds = rangeBounds(preset, today);
    if (bounds !== null && bounds.from === filter.from && bounds.to === filter.to) {
      return preset;
    }
  }
  return 'custom';
}

/**
 * A sentence describing what is on screen.
 *
 * Not decoration. A filtered table that looks like the whole table is how
 * somebody concludes a request is missing, and this screen is the one people
 * come to when they think exactly that.
 */
export function filterSummary(
  filter: AllRequestFilter,
  rows: number,
  employeeName: string,
): string {
  const count = `${String(rows)} ${rows === 1 ? 'request' : 'requests'}`;
  const parts: string[] = [];
  if (employeeName !== '') {
    parts.push(employeeName);
  }
  if (filter.status !== '') {
    parts.push(filter.status.toLowerCase().replace('_', ' '));
  }
  if (filter.from !== '' && filter.to !== '') {
    parts.push(`${filter.from} to ${filter.to}`);
  } else if (filter.from !== '') {
    parts.push(`from ${filter.from}`);
  } else if (filter.to !== '') {
    parts.push(`up to ${filter.to}`);
  }
  return parts.length === 0 ? `${count}, everyone, all time` : `${count} · ${parts.join(' · ')}`;
}

/**
 * Can this row be edited or removed, and if not, why not.
 *
 * The rules live in the database — `admin_edit_leave_request` takes PENDING
 * and APPROVED, `delete_leave_request` takes PENDING alone — and this is the
 * screen agreeing with them rather than a second opinion. A button that is
 * going to be refused should not be offered.
 */
export function rowActions(status: string): { canEdit: boolean; canDelete: boolean } {
  return {
    canEdit: status === 'PENDING' || status === 'APPROVED',
    canDelete: status === 'PENDING',
  };
}
